import React, { useRef, useEffect, useState } from 'react';
import { useNovaChat } from '../../contexts/NovaChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';

/**
 * NovaChat - UGC Creator AI Assistant
 * Floating chat interface for UGC pages only
 * Amber/creator-themed branding, distinct from Lumo (sales coach)
 */
const NovaChat = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isOpen,
    messages,
    isTyping,
    unreadCount,
    toggleChat,
    closeChat,
    sendMessage,
    executeAction
  } = useNovaChat();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [messages, isOpen, isTyping]);
  
  // Don't render if user not authenticated
  if (!user) return null;

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue, location?.pathname);
      setInputValue('');
    }
  };

  const handleActionClick = (action) => {
    if (action.type === 'focus_input') {
      inputRef.current?.focus();
    } else if (action.type === 'navigate') {
      // Use React Router navigation instead of full page reload
      navigate(action.path);
    } else {
      executeAction(action);
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-8rem)] bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 rounded-3xl shadow-2xl border border-white/10 flex flex-col overflow-hidden z-50 backdrop-blur">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-500/20 to-white/5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  N
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-slate-900" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Nova</h3>
                <p className="text-amber-200/80 text-xs">UGC Creator Coach</p>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close chat"
            >
              <Icon name="X" size={18} className="text-white/70" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3',
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.type === 'nova' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    N
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[80%] rounded-2xl px-4 py-3 text-sm',
                    message.type === 'user'
                      ? 'bg-amber-400 text-slate-950 rounded-tr-sm'
                      : 'bg-white/5 text-white border border-white/10 rounded-tl-sm'
                  )}
                >
                  <p className="whitespace-pre-wrap">{message.message}</p>
                  
                  {/* Action Buttons */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.actions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleActionClick(action)}
                          className="px-3 py-1.5 text-xs rounded-lg bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 border border-amber-400/30 transition-colors font-medium"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white text-sm flex-shrink-0">
                    <Icon name="User" size={16} />
                  </div>
                )}
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  N
                </div>
                <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3 border border-white/10">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="px-6 py-4 bg-white/5 border-t border-white/10">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask Nova about scripts, brands, hooks..."
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="w-10 h-10 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:hover:bg-amber-400 flex items-center justify-center transition-colors"
                aria-label="Send message"
              >
                <Icon name="Send" size={18} className="text-slate-950" />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={toggleChat}
        className={cn(
          'fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all z-50',
          'bg-gradient-to-br from-amber-400 to-orange-500 hover:scale-110',
          'border-2 border-white/20'
        )}
        aria-label="Toggle Nova chat"
      >
        {isOpen ? (
          <Icon name="X" size={24} className="text-white" />
        ) : (
          <>
            <div className="relative">
              <span className="text-2xl font-bold text-white">N</span>
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-slate-900">
                  {unreadCount}
                </div>
              )}
            </div>
          </>
        )}
      </button>
    </>
  );
};

export default NovaChat;
